<template>
  <TransitionWrapper
    name="slideVerticalFloating"
  >
    <div
      v-if="showChecklist"
      :class="$style.onboardingSidebar"
    >
      <div :class="$style.onboardingHeader">
        <div :class="$style.btnContainer">
          <Button
            hasNoBackground
            text="Hide"
            type="informational"
            @click="handleClose"
          />
        </div>
        <HeadingMedium>
          Implementation Checklist
        </HeadingMedium>
        <HeadingExtraSmall v-if="!checklists.length">
          Your customized getting started guide
        </HeadingExtraSmall>
        <HeadingExtraSmall v-else>
          {{ percentage }}% complete
        </HeadingExtraSmall>
        <ProgressBar
          :class="$style.checklistProgressBar"
          :numComplete="totalComplete"
          :numItems="progressBarTotal"
        />
        <IconButton
          :class="$style.editChecklistButton"
          hasOutline
          icon="edit"
          text="Edit Checklist"
          type="secondary"
          @click="handleEditChecklist"
        />
      </div>
      <div :class="$style.mainContainer">
        <EmptyMessage v-if="!checklists.length" />
        <ImplementationChecklistItem
          v-for="(checklist, index) in checklists"
          :key="checklist.feature"
          :checklist="checklist"
          :currentIndex="index"
          :showChecklistResource="false"
          :showRemoveButton="true"
          :startCollapsed="shouldStartCollapsed(checklist, index)"
          @event="handleEvent"
          @expandedIndex="handleExpandedToggle"
        />
        <RecommendedChecklistItem
          v-for="checklist in recommendedChecklists"
          :key="checklist.feature"
          :checklist="checklist"
          :showChecklistResource="false"
          @event="handleEvent"
        />
        <RecommendedPackage />
        <ProductTourLaunchButton
          :class="$style.productTourButton"
          :location="$options.productTourLocation"
        />
      </div>
    </div>
  </TransitionWrapper>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  Button,
  HeadingExtraSmall,
  HeadingMedium,
  IconButton,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { implementationChecklistRegion } from 'client/components/ImplementationChecklist/ImplementationChecklistConstants';
import { mapGetters } from 'vuex';
import {
  productTourLabels,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import Cookies from 'js-cookie';
import CustomizableDashboardMixin from 'client/Discover/mixins/CustomizableDashboardMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import LocalStorageService from 'client/util/LocalStorageService';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import ProductTourLaunchButton from 'client/components/ProductTour/ProductTourLaunchButton.vue';
import RecommendedChecklistItem from 'client/components/ImplementationChecklist/components/RecommendedChecklistItem';
import Util from 'client/util/Util';

import ChecklistMixin from './mixins/ChecklistMixin';
import EmptyMessage from './components/EmptyMessage.vue';
import ImplementationChecklistItem from './components/ImplementationChecklistItem.vue';
import ProgressBar from './components/ProgressBar.vue';
import RecommendedPackage from './components/RecommendedPackage.vue';

const {
  ItemTypeConsts,
  LoginModalConsts,
  StoreModuleMap,
} = Constants;

const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const {
  excludeModalTestingCookie,
} = LoginModalConsts;

export default {
  name: 'ImplementationChecklist',

  components: {
    Button,
    EmptyMessage,
    HeadingExtraSmall,
    HeadingMedium,
    IconButton,
    ImplementationChecklistItem,
    ProductTourLaunchButton,
    ProgressBar,
    RecommendedChecklistItem,
    RecommendedPackage,
    TransitionWrapper,
  },

  mixins: [CustomizableDashboardMixin, ChecklistMixin],

  LocalStorageService,
  OptimizelyHelper,
  productTourLocation: productTourLabels.checklistLocation,

  props: {
    visible: {
      default: true,
      type: Boolean,
    },
  },

  data() {
    return {
      announcementSeen: false,
    };
  },

  computed: {

    ...mapGetters(organizationsCollection, {
      orgIsOlderThanDays: 'getOrgIsOlderThanDays',
    }),

    ...mapGetters(settingsModule, ['isCommandRunner']),

    showChecklist() {
      const excludeTesting = !!Cookies.get(excludeModalTestingCookie);

      return this.visible
        && !this.isCommandRunner
        && !excludeTesting;
    },
  },

  created() {
    this.checkExistingChecklists();
  },

  methods: {
    handleClose() {
      this.$emit('hide');

      UiEvents.triggerLinkClicked({
        description: 'Hides the implementation checklist sidebar.',
        page: this.$page,
        region: implementationChecklistRegion,
        section: 'header',
        text: 'Hide',
      });
    },

    handleEditChecklist() {
      Util.redirect('#/discover');

      UiEvents.triggerButtonClicked({
        description: 'Navigates user to Discover Page.',
        page: this.$page,
        region: implementationChecklistRegion,
        section: 'header',
        text: 'Edit Checklist',
        toUrl: '#/discover',
      });
    },

    handleEvent(event) {
      UiEvents.triggerCheckboxClicked(event);
      if (this.totalComplete === this.total) {
        EventHelper.triggerImplementationChecklistComplete(event.section, event.text);
      }
    },

    handleExpandedToggle(index) {
      if (index === this.expandedIndex) {
        this.updateChecklistExpandedIndex(null);
      } else {
        this.updateChecklistExpandedIndex(index);
      }
    },
  },
};
</script>

<style module>
@value (panelSpeed) from '~css/transitions.css';
@value ( max-width-large ) from '~css/breakpoints.css';

.onboardingSidebar {
  --sidebar-max-width: 386px;

  background-color: var(--jcGrey100);
  display: none;
  grid-area: onboardingSidebar;
  height: 100%;
  overflow-y: auto;
  width: var(--sidebar-max-width);
}

.onboardingHeader {
  background-color: var(--jcGrey200);
  padding: 20px;
  text-align: center;
}

.btnContainer {
  text-align: end;
}

.editChecklistButton {
  width: 100%;
}

.checklistProgressBar {
  padding-top: 0;
}

.mainContainer {
  padding: 20px;
}

.productTourButton {
  margin-top: var(--jc-spacer);
}

@media (min-width: max-width-large) {
  .onboardingSidebar {
    display: block;
  }
}
</style>
